import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, UserCog, Camera, BadgeCheck } from "lucide-react";
import { CardGridSkeleton } from "@/components/Skeletons";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PlanChangeDialog from "@/components/PlanChangeDialog";
import { toast } from "sonner";
import { PANEL_CLS, PAGE_EYEBROW_CLS, PAGE_H1_CLS, PRIMARY_BTN_CLS } from "@/components/premium";

function StaffAvatar({ user, size = 44, onUpload, removable, onRemove }) {
  const fileRef = useRef(null);
  const initials = user.name.trim().split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const px = `${size}px`;
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Velg en bildefil");
    if (file.size > 200 * 1024) return toast.error("Bildet er for stort (max 200KB)");
    const reader = new FileReader();
    reader.onload = () => onUpload?.(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="relative shrink-0 group" style={{ width: px, height: px }}>
      {user.avatar_url ? (
        <img
          src={user.avatar_url}
          alt={user.name}
          className="w-full h-full rounded-full object-cover border border-border"
        />
      ) : (
        <div className="w-full h-full rounded-full bg-accent flex items-center justify-center text-primary font-heading font-bold">
          {initials}
        </div>
      )}
      {onUpload && (
        <>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="absolute inset-0 rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            aria-label="Endre profilbilde"
            data-testid={`avatar-upload-trigger-${user.id}`}
          >
            <Camera className="w-4 h-4" />
          </button>
          <input
            ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={handleFile} data-testid={`avatar-upload-input-${user.id}`}
          />
          {removable && user.avatar_url && (
            <button
              type="button"
              onClick={onRemove}
              className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-[10px]"
              aria-label="Fjern bilde"
              data-testid={`avatar-remove-${user.id}`}
            >×</button>
          )}
        </>
      )}
    </div>
  );
}

export default function Staff() {
  const { user: me, refresh } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [biz, setBiz] = useState(null);
  const [planChangeOpen, setPlanChangeOpen] = useState(false);
  // Two-step cost confirmation for paying plans. `addCostOpen` shows the
  // +99 kr/mnd notice BEFORE the "Legg til ansatt" form opens. `removeTarget`
  // shows the -99 kr/mnd reduction notice before a DELETE goes through.
  const [addCostOpen, setAddCostOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Plans that include the +99 kr/mnd extra-staff billing line. Demo and
  // Basic don't (Demo has 1 free showcase slot; Basic blocks staff
  // entirely). Pro and Premium both bill per extra Ansatt.
  const isPaidStaffPlan = biz?.plan === "pro" || biz?.plan === "premium";

  const load = async () => {
    setLoading(true);
    try {
      const [staffRes, bizRes] = await Promise.all([
        api.get("/staff"),
        api.get("/business"),
      ]);
      setList(staffRes.data);
      setBiz(bizRes.data);
    }
    catch { toast.error("Kunne ikke laste personal"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const uploadAvatar = async (userId, dataUrl) => {
    try {
      await api.put(`/staff/${userId}/avatar`, { data_url: dataUrl });
      toast.success("Profilbilde oppdatert");
      load();
      // If we updated our own avatar, refresh auth context so sidebar reflects it
      if (me?.id === userId) refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke laste opp");
    }
  };

  const removeAvatar = async (userId) => {
    try {
      await api.delete(`/staff/${userId}/avatar`);
      toast.success("Profilbilde fjernet");
      load();
      if (me?.id === userId) refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke fjerne");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error("Fyll inn alle felt");
    if (form.password.length < 6) return toast.error("Passord må være minst 6 tegn");
    setSaving(true);
    try {
      await api.post("/staff", form);
      toast.success("Ansatt lagt til");
      setOpen(false); setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke lagre");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    setRemoving(true);
    try { await api.delete(`/staff/${id}`); toast.success("Fjernet"); setRemoveTarget(null); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Kunne ikke fjerne"); }
    finally { setRemoving(false); }
  };

  // Triggered from the "Ny ansatt" button. On Pro/Premium we open the
  // cost notice first; on Demo we skip straight to the form (the showcase
  // slot is free).
  const handleAddClick = () => {
    if (isPaidStaffPlan) {
      setAddCostOpen(true);
    } else {
      setOpen(true);
    }
  };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className={PAGE_EYEBROW_CLS}>Personal</p>
          <h1 className={PAGE_H1_CLS}>Team</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Legg til ansatte som kan logge inn og styre kalenderen.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button
            className={PRIMARY_BTN_CLS}
            disabled={biz?.plan === "basic"}
            onClick={handleAddClick}
            data-testid="new-staff-btn"
          >
            <Plus className="w-4 h-4" strokeWidth={2.25} /> Ny ansatt
          </Button>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Legg til ansatt</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Navn</Label><Input className="rounded-xl mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" /></div>
              <div><Label>E-post</Label><Input type="email" className="rounded-xl mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" /></div>
              <div><Label>Midlertidig passord</Label><PasswordInput className="rounded-xl mt-1.5" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 tegn" data-testid="staff-password-input" toggleTestId="staff-password-toggle" /></div>
              <p className="text-xs text-muted-foreground">Den ansatte kan endre passord ved første innlogging via "Glemt passord".</p>
              <Button type="submit" disabled={saving} className={`w-full ${PRIMARY_BTN_CLS} h-11`} data-testid="staff-submit-btn">
                {saving ? "Lagrer..." : "Legg til"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {biz?.plan === "basic" && (
        // Basic = 1 Leder only. Match the Demo hint visual style so the
        // limit is visible up front (the disabled "Ny ansatt" button alone
        // is too subtle). Include a "Be om oppgradering til Pro" CTA so
        // hitting the cap converts into a Super Admin lead instead of a
        // dead-end disabled button.
        <div
          className="rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-3 text-sm"
          data-testid="basic-staff-hint"
        >
          <p className="font-semibold text-amber-900 dark:text-amber-100">
            Basic: 1 bruker / leder
          </p>
          <p className="text-amber-900/85 dark:text-amber-200/90 mt-0.5">
            Flere ansatte er tilgjengelig i Pro.
          </p>
          <Button
            size="sm"
            className="mt-3 rounded-xl"
            onClick={() => setPlanChangeOpen(true)}
            data-testid="basic-upgrade-pro-btn"
          >
            Be om oppgradering til Pro
          </Button>
        </div>
      )}

      {isPaidStaffPlan && (
        <p className="text-xs text-muted-foreground" data-testid="paid-staff-pricing-hint">
          Ekstra ansatte koster <span className="font-semibold text-foreground">99 kr/mnd eks. mva</span> per ansatt.
        </p>
      )}

      {loading ? (
        <CardGridSkeleton count={3} />
      ) : list.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border-2 border-dashed border-border" data-testid="staff-empty">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-accent items-center justify-center text-primary mb-3">
            <UserCog className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <p className="font-heading text-xl font-semibold">Ingen ansatte enda</p>
          <p className="text-muted-foreground mt-1">Legg til kollegaer som kan hjelpe deg med kalenderen.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((u) => (
            <Card key={u.id} className={`${PANEL_CLS} card-hover hover:ring-emerald-500/20`} data-testid="staff-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <StaffAvatar
                    user={u}
                    size={48}
                    onUpload={(dataUrl) => uploadAvatar(u.id, dataUrl)}
                    removable
                    onRemove={() => removeAvatar(u.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{u.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <Badge variant="outline" className="rounded-full text-xs">
                    {u.role === "owner"
                      ? (biz?.is_admin ? "ClientFlow-admin" : "Leder")
                      : "Ansatt"}
                  </Badge>
                </div>
                {u.role === "staff" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl mt-4 w-full"
                    onClick={() => setRemoveTarget(u)}
                    data-testid="remove-staff-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Fjern
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PlanChangeDialog
        open={planChangeOpen}
        onOpenChange={setPlanChangeOpen}
        business={biz}
        userEmail={me?.email}
        userName={me?.name}
        defaultInterest="pro"
        source="staff-basic-upgrade-pro"
      />

      {/* Pro/Premium: cost confirmation BEFORE the add-form opens. Makes
          the +99 kr/mnd line visible and explicit so the Leder always
          knows what an extra Ansatt actually costs. */}
      <AlertDialog open={addCostOpen} onOpenChange={setAddCostOpen}>
        <AlertDialogContent className="rounded-2xl" data-testid="add-staff-cost-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-primary" strokeWidth={1.75} />
              Legg til ekstra ansatt
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Du legger til en ekstra ansatt på <span className="font-semibold text-foreground capitalize">{biz?.plan}</span>-planen.
                </p>
                <div className="rounded-xl border border-border bg-accent/40 px-3 py-2">
                  <p className="text-foreground font-semibold tabular-nums">
                    + 99 kr/mnd eks. mva
                  </p>
                  <p className="text-xs mt-0.5">
                    Lagt til på neste fakturaperiode.
                  </p>
                </div>
                <p className="text-xs">
                  Du kan fjerne ansatte når som helst. Endringer i kostnad slår inn ved neste fakturering.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl h-10" data-testid="add-staff-cost-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              className={PRIMARY_BTN_CLS}
              onClick={() => { setAddCostOpen(false); setOpen(true); }}
              data-testid="add-staff-cost-confirm"
            >
              Fortsett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove confirmation. Pro/Premium see the price-reduction note;
          Demo + Basic see the minimal copy. */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="remove-staff-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Fjerne {removeTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Brukeren vil ikke lenger kunne logge inn. Tidligere bookinger og data forblir uendret.
                </p>
                {isPaidStaffPlan && (
                  <div className="rounded-xl border border-border bg-accent/40 px-3 py-2" data-testid="remove-staff-pricing-note">
                    <p className="text-foreground font-semibold tabular-nums">
                      − 99 kr/mnd eks. mva
                    </p>
                    <p className="text-xs mt-0.5">
                      Prisreduksjonen gjelder fra neste fakturaperiode.
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="remove-staff-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              disabled={removing}
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => removeTarget && remove(removeTarget.id)}
              data-testid="remove-staff-confirm"
            >
              {removing ? "Fjerner..." : "Fjern"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
